import {
  defaultSite,
  generateId,
  useSite,
} from "@inrange/building-manager-api-client";
import {
  AllowStringForNumbers,
  RecursivePartial,
  Site,
  SiteAllowStringValues,
  TariffConfig,
} from "@inrange/building-manager-api-client/models-site";
import { SiteCalculationsProvider } from "@inrange/shared-components";
import { Modal, ModalView } from "@inrange/theme-components";
import deepmerge from "deepmerge";
import cloneDeep from "lodash/cloneDeep";
import { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { applySiteUpdate, overwriteMerge } from "../site/site-utils";
import SupplierTariff from "../site/siteSections/SupplierTariff";

const BulkEditSupplierTariff = ({
  selectedSiteIds,
  onSitesUpdate,
  onClose,
}: {
  selectedSiteIds: Set<string>;
  onSitesUpdate: (payload: any) => void;
  onClose: () => void;
}) => {
  const [saveNote, setSaveNote] = useState("");
  const { fetchSiteValues } = useSite({ app: "admin" });

  const clearDefaultValues = (
    site: RecursivePartial<
      AllowStringForNumbers<{
        id: string;
        tariffConfig: TariffConfig;
      }>
    >
  ) => {
    site.tariffConfig!.supplier!.peakOffPeak!.overrideRatesMap![
      "peak"
    ]!.tariff = undefined;
    site.tariffConfig!.supplier!.contractLength = undefined;
    site.tariffConfig!.supplier!.contractEnergyPriceInflationRate = undefined;
    site.tariffConfig!.supplier!.duosInflationRate = undefined;
    return site;
  };

  const [previewSite, setPreviewSite] = useState<
    RecursivePartial<
      AllowStringForNumbers<{
        id: string;
        tariffConfig: TariffConfig;
      }>
    >
  >(
    cloneDeep(
      clearDefaultValues(
        defaultSite(
          fetchSiteValues.data!.site,
          fetchSiteValues.data!.options,
          generateId(),
          1,
          1,
          undefined,
          undefined
        )
      )
    )
  );
  const [site, setSite] = useState<
    RecursivePartial<
      AllowStringForNumbers<{
        id: string;
        tariffConfig: TariffConfig;
      }>
    >
  >({
    id: "bulk-preview",
    tariffConfig: { supplier: {} },
  });
  return (
    <Modal>
      <ModalView
        id="bulk-edit-supplier-tariff"
        title={`Edit Supplier tariffs, charges, and savings for ${selectedSiteIds.size} site${selectedSiteIds.size === 1 ? "" : "s"}`}
        fontWeight="500"
        fontSize="20px"
        width="800px"
        titlePadding="0 0 10px 0"
      >
        <SiteCalculationsProvider
          site={site as unknown as Partial<Site>}
          originalSite={{}}
          app="admin"
          isBulkEdit={true}
        >
          <SupplierTariff
            site={previewSite as unknown as SiteAllowStringValues}
            setSite={(update) => {
              // Create a clone to make sure that values
              // being passed into the second deepmerge are not mutated
              const updateClone = cloneDeep(update);
              setPreviewSite((prevSite) =>
                deepmerge(prevSite, update, {
                  arrayMerge: overwriteMerge,
                })
              );
              setSite((prevSite) => applySiteUpdate(prevSite, updateClone));
            }}
            isBulkEdit={true}
          />
          <Form.Control
            className="mt-3"
            as="textarea"
            rows={2}
            placeholder="Add a short note describing the change made"
            value={saveNote}
            onChange={(e) => setSaveNote(e.target.value)}
            autoFocus
            data-testid="site-save-notes"
          />
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              flexDirection: "row",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Button variant="inrangesecondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => {
                // site was populated using defaultSite(...) so we need to extract only
                // the parts we want to update
                const update = {
                  site: {
                    tariffConfig: {
                      supplier: site.tariffConfig!.supplier,
                    },
                  },
                };
                const updates = Array.from(selectedSiteIds).reduce(
                  (acc, siteId) => {
                    acc[siteId] = update;
                    if (saveNote.trim().length > 0) {
                      acc[siteId].userNote = saveNote.trim();
                    }
                    return acc;
                  },
                  {} as { [key: string]: any }
                );
                onSitesUpdate(updates);
              }}
            >
              Edit Supplier tariffs, charges, and savings
            </Button>
          </div>
        </SiteCalculationsProvider>
      </ModalView>
    </Modal>
  );
};

export default BulkEditSupplierTariff;
